import { useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { ChatCenteredText, Plus, PencilSimple, Trash, X } from "@phosphor-icons/react";

const EMPTY = { title: "", subject: "", text: "" };

function SnippetForm({ value, placeholders, onCancel, onSaved, snippetId }) {
  const [f, setF] = useState(value);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = snippetId ? await API.put(`/inbox/snippets/${snippetId}`, f) : await API.post("/inbox/snippets", f);
      toast.success(snippetId ? "Snippet updated" : "Snippet added"); onSaved(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't save"); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border border-primary/40 bg-primary/5 p-3 space-y-2" data-testid="inbox-snippet-form">
      <div className="grid sm:grid-cols-2 gap-2">
        <div><Label className="text-xs">Title (what staff see in the picker)</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Engineer booked" className="h-8 text-xs" data-testid="inbox-snippet-title" /></div>
        <div><Label className="text-xs">Subject <span className="text-muted-foreground font-normal">(optional — blank keeps "Re: …")</span></Label><Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="Your request {reference} — engineer booked" className="h-8 text-xs" data-testid="inbox-snippet-subject" /></div>
      </div>
      <div><Label className="text-xs">Reply text</Label><Textarea rows={5} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} placeholder={"Hi {first_name},\n\n…\n\n{my_name}\n{company}"} className="text-xs" data-testid="inbox-snippet-text" /></div>
      <p className="text-[11px] text-muted-foreground">Placeholders: {placeholders.map((p) => <span key={p.key} title={p.help} className="font-mono mr-1.5 cursor-help">{`{${p.key}}`}</span>)}</p>
      <div className="flex gap-2 justify-end">
        <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button type="button" size="sm" className="h-7 text-xs" onClick={save} disabled={busy || !f.title.trim() || !f.text.trim()} data-testid="inbox-snippet-save">{busy ? "Saving…" : snippetId ? "Save changes" : "Add snippet"}</Button>
      </div>
    </div>
  );
}

/** Admin-managed quick-reply snippets shared by everyone with inbox access. */
export default function InboxSnippetsSection() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | snippet id
  const load = () => API.get("/inbox/snippets").then((r) => setData(r.data)).catch(() => setData({ snippets: [], placeholders: [], max: 50 }));
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const remove = async (s) => {
    if (!window.confirm(`Delete the snippet "${s.title}"?`)) return;
    try { await API.delete(`/inbox/snippets/${s.id}`); toast.success("Snippet deleted"); load(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't delete"); }
  };
  return (
    <div className="space-y-2" data-testid="inbox-snippets-section">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1"><ChatCenteredText size={13} /> Quick-reply snippets</p>
          <p className="text-xs text-muted-foreground">Ready-made replies staff drop into an inbox reply with one tap. Placeholders fill in from the email ({`{first_name}`}, {`{reference}`}, {`{my_name}`}…). {data.snippets.length} of {data.max}.</p>
        </div>
        {editing !== "new" && <Button type="button" size="sm" variant="outline" className="h-7 text-xs shrink-0" onClick={() => setEditing("new")} disabled={data.snippets.length >= data.max} data-testid="inbox-snippet-add-btn"><Plus size={12} className="mr-1" /> New snippet</Button>}
      </div>
      {editing === "new" && <SnippetForm value={EMPTY} placeholders={data.placeholders} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <div className="space-y-1.5" data-testid="inbox-snippet-list">
        {data.snippets.map((s) => editing === s.id
          ? <SnippetForm key={s.id} snippetId={s.id} value={{ title: s.title, subject: s.subject || "", text: s.text }} placeholders={data.placeholders} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
          : (
            <div key={s.id} className="group rounded-md border border-border p-2.5 flex items-start gap-2" data-testid={`inbox-snippet-${s.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate" data-testid={`inbox-snippet-title-${s.id}`}>{s.title}</p>
                {s.subject && <p className="text-[11px] text-muted-foreground truncate">Subject: {s.subject}</p>}
                <p className="text-xs text-muted-foreground line-clamp-2">{s.text.replace(/\s*\n+\s*/g, " ")}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditing(s.id)} aria-label="Edit" data-testid={`inbox-snippet-edit-${s.id}`}><PencilSimple size={13} /></Button>
                <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => remove(s)} aria-label="Delete" data-testid={`inbox-snippet-delete-${s.id}`}><Trash size={13} /></Button>
              </div>
            </div>
          ))}
        {!data.snippets.length && editing !== "new" && <p className="text-xs text-muted-foreground rounded-md border border-dashed border-border p-3 text-center" data-testid="inbox-snippet-empty"><X size={12} className="inline mr-1" />No snippets yet — add one above.</p>}
      </div>
    </div>
  );
}
